import React, { useEffect, useState, useCallback } from 'react';
import { LocaSmartLayout } from '../../../components/locasmart-layout';
import Listagem, { FiltroDropdownOpcao } from '../../../components/listagem';
import { NFServiceService } from '../../../services/NFServiceService';
import { StatusNFSe, NFService } from '../../../services/tipos';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { FaFileCode, FaBan, FaSync, FaFileInvoiceDollar, FaEye, FaEdit } from 'react-icons/fa';
import { GridColDef } from '@mui/x-data-grid';
import { obterIdTenent } from '../../../services/seguranca';
import { useAppSelector } from '../../../store/hooks';
import ModalEmitirNota from './ModalEmitirNota';
import ModalDetalhesNota from './ModalDetalhesNota';
import ModalNovaNota from './ModalNovaNota';
import ModalCancelarNota from './ModalCancelarNota';
import ModalEditarAgendamento from './ModalEditarAgendamento';
import { confirm } from '../../../components/confirm';
import { ConfiguracaoEmpresa } from '../../../services/tipos';
import api from '../../../services/api';

const NotasFiscaisListagem: React.FC = () => {
    const location = useLocation();
    const token = useAppSelector((state) => state.token);

    const [idPagamentoFiltro, setIdPagamentoFiltro] = useState<string | null>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get('idPagamento');
    });

    const [items, setItems] = useState<NFService[]>([]);
    const [loading, setLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

    const [dataInicio, setDataInicio] = useState<Date | null>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get('idPagamento') ? null : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    });

    const [dataFim, setDataFim] = useState<Date | null>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get('idPagamento') ? null : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    });

    const [statusFiltro, setStatusFiltro] = useState<any>('');

    const [showModalEmitir, setShowModalEmitir] = useState(false);
    const [dadosParaEmitir, setDadosParaEmitir] = useState<{ idPagamento?: string, idLocatario?: string, valor?: number, idNf?: string } | null>(null);
    const [loadingEmission, setLoadingEmission] = useState(false);
    const [showModalDetalhes, setShowModalDetalhes] = useState(false);
    const [notaSelecionada, setNotaSelecionada] = useState<NFService | null>(null);
    const [showModalNovaNota, setShowModalNovaNota] = useState(false);
    const [showModalCancelar, setShowModalCancelar] = useState(false);
    const [showModalEditarAgendamento, setShowModalEditarAgendamento] = useState(false);
    const [notaParaCancelar, setNotaParaCancelar] = useState<NFService | null>(null);
    const [configuracao, setConfiguracao] = useState<ConfiguracaoEmpresa | null>(null);
    const [emissaoLiberada, setEmissaoLiberada] = useState<boolean>(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const [configResp, empresaResp] = await Promise.all([
                    api.get(`ConfiguracaoPorEmpresa/${obterIdTenent(token)}`),
                    api.get(`Empresa/${obterIdTenent(token)}`)
                ]);
                setConfiguracao(configResp.data);
                setEmissaoLiberada(
                    empresaResp.data?.emissaoNotaFiscalLiberada
                    || configResp.data?.empresa?.emissaoNotaFiscalLiberada
                    || false
                );
            } catch (error) {
                console.error("Erro ao carregar configuracao");
            }
        };
        if (token) fetchConfig();
    }, [token]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const idPagamento = searchParams.get('idPagamento');
        
        if (idPagamento !== idPagamentoFiltro) {
            setIdPagamentoFiltro(idPagamento);
            if (idPagamento) {
                setDataInicio(null);
                setDataFim(null);
            }
        }
    }, [location.search]);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            if (idPagamentoFiltro) {
                const item = await NFServiceService.obterPorPagamento(idPagamentoFiltro);
                if (item) {
                    setItems([item]);
                    setRowCount(1);
                } else {
                    setItems([]);
                    setRowCount(0);
                }
            } else {
                const filter = {
                    idEmpresa: obterIdTenent(token),
                    page: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    dataInicio: dataInicio || undefined,
                    dataFim: dataFim || undefined,
                    status: statusFiltro !== '' ? Number(statusFiltro) : undefined
                };
                const result = await NFServiceService.listarPaginado(filter);
                setItems(result.items);
                setRowCount(result.total);
            }
        } catch (error) {
            console.error(error);
            toast.error("Erro ao carregar notas fiscais.");
        } finally {
            setLoading(false);
        }
    }, [idPagamentoFiltro, paginationModel, dataInicio, dataFim, statusFiltro, token]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const handleBuscar = () => {
        setIdPagamentoFiltro(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleBaixarXml = async (row: NFService) => {
        try {
            if(row.status === StatusNFSe.Erro) {
                toast.error("Não é possível obter o XML de uma Nota Fiscal com erro!")
                return;
            }
            const blob = await NFServiceService.baixarXml(row.id);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `nfse_${row.numeroNota || row.protocolo}.xml`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error("Erro ao baixar XML.");
        }
    };

    const handleCancelarClick = async (row: NFService) => {
        if (row.status === StatusNFSe.Cancelada) {
            toast.warning("Esta nota já está cancelada.");
            return;
        }

        if (row.status === StatusNFSe.Processando) {
            toast.warning("Aguarde o processamento da Nota Fiscal para realizar o cancelamento!");
            return;
        }

        if (row.status === StatusNFSe.Agendada) {
            if (await confirm("Deseja realmente cancelar este agendamento?")) {
                handleConfirmarCancelamento(row.id, "Cancelamento de Agendamento");
            }
            return;
        }

        setNotaParaCancelar(row);
        setShowModalCancelar(true);
    };

    const handleConfirmarCancelamento = async (idOrMotivo: string, motivoIfId?: string) => {
        let id = notaParaCancelar?.id;
        let motivo = idOrMotivo;

        if (motivoIfId) {
            id = idOrMotivo;
            motivo = motivoIfId;
        }

        if (!id) return;

        try {
            await NFServiceService.cancelar(id, motivo);
            toast.success("Solicitação processada com sucesso.");
            setShowModalCancelar(false);
            carregarDados();
        } catch (error: any) {
            toast.error("Erro ao cancelar: " + (error.response?.data || error.message));
        }
    };

    const handleEditarClick = (row: NFService) => {
        setNotaSelecionada(row);
        setShowModalEditarAgendamento(true);
    };
    const handleConsultar = async (row: NFService) => {
        try {
            setLoading(true);
            await NFServiceService.consultar(row.id);
            toast.success("Nota atualizada.");
            carregarDados();
        } catch (error) {
            toast.error("Erro ao consultar.");
        } finally {
            setLoading(false);
        }
    };

    const handleAbrirEmitir = (row: NFService) => {
        setDadosParaEmitir({
            idPagamento: row.idPagamento,
            idLocatario: row.idLocatario,
            valor: row.valor,
            idNf: row.id
        });
        setShowModalEmitir(true);
    };

    const onEmitir = async (idServico: string) => {
        if (!dadosParaEmitir) return;
        setLoadingEmission(true);
        try {
            await NFServiceService.emitir(
                dadosParaEmitir.idPagamento, 
                idServico, 
                obterIdTenent(token),
                dadosParaEmitir.idLocatario,
                dadosParaEmitir.valor,
                undefined,
                dadosParaEmitir.idNf
            );
            toast.success("Nota enviada para processamento.");
            setShowModalEmitir(false);
            carregarDados();
        } catch (error: any) {
            const mensagem = error.response?.data.Mensagem != undefined ? error.response?.data.Mensagem : error.response?.data.message ? error.response?.data.message : 'Erro desconhecido';
             toast.error("Erro ao emitir: " + mensagem);
        } finally {
            setLoadingEmission(false);
        }
    };

    const handleVisualizar = (row: NFService) => {
        setNotaSelecionada(row);
        setShowModalDetalhes(true);
    };

    const handleCriar = () => {
        setShowModalNovaNota(true);
    };

    const colunas: GridColDef[] = [
        { 
            field: 'numeroNota', 
            headerName: 'Número (RPS)', 
            flex: 0.8,
            renderCell: (params) => params.value || '-'
        },
        { 
            field: 'protocolo', 
            headerName: 'Protocolo', 
            flex: 0.8,
            renderCell: (params) => params.value || '-'
        },
        { 
            field: 'nomeLocatario', 
            headerName: 'Locatário', 
            flex: 1.5 
        },
        { 
            field: 'valor', 
            headerName: 'Valor', 
            flex: 0.8,
            renderCell: (params) => params.value ? Number(params.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''
        },
        { 
            field: 'descricaoPagamento', 
            headerName: 'Descrição', 
            flex: 1.5 
        },
        { 
            field: 'dataHoraEmissao', 
            headerName: 'Emissão', 
            flex: 1,
            renderCell: (params) => params.value ? new Date(params.value).toLocaleString() : '-'
        },
        { 
            field: 'idService', 
            headerName: 'Serviço', 
            flex: 1,
            renderCell: (params) => {
                return ""
            }
        },
        { 
            field: 'status', 
            headerName: 'Status', 
            flex: 1,
            renderCell: (params) => {
                const status = params.value as StatusNFSe;
                let text = '';
                let backgroundColor = '#808080';

                switch(status) {
                    case StatusNFSe.Processando: 
                        backgroundColor = '#FFC107'; text = 'Processando'; break;
                    case StatusNFSe.Emitida: 
                        backgroundColor = '#4CAF50'; text = 'Emitida'; break;
                    case StatusNFSe.Cancelada: 
                        backgroundColor = '#f44336'; text = 'Cancelada'; break;
                    case StatusNFSe.Erro: 
                        backgroundColor = '#f44336'; text = 'Erro'; break;
                    case StatusNFSe.Agendada: 
                        backgroundColor = '#007bff'; text = 'Agendada'; break;
                }
                
                return (
                    <span 
                        onClick={() => handleVisualizar(params.row)}
                        style={{
                            backgroundColor,
                            color: 'white',
                            padding: '5px 10px',
                            borderRadius: '5px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            cursor: 'pointer',
                            display: 'inline-block',
                            minWidth: '80px'
                        }}
                    >
                        {text}
                    </span>
                );
            }
        },
    ];

    const opcoesStatus: FiltroDropdownOpcao[] = [
        { label: 'Processando', value: StatusNFSe.Processando },
        { label: 'Emitida', value: StatusNFSe.Emitida },
        { label: 'Cancelada', value: StatusNFSe.Cancelada },
        { label: 'Erro', value: StatusNFSe.Erro },
        { label: 'Agendada', value: StatusNFSe.Agendada },
    ];

    return (
        <LocaSmartLayout>
             <Listagem
                nomeEntidade="Notas Fiscais"
                IconeEntidade={FaFileInvoiceDollar}
                carregando={loading}
                linhas={items}
                colunas={colunas}
                nomePropriedade="protocolo"
                handleCriar={emissaoLiberada ? handleCriar : () => {
                    toast.info('Emissão de NFSe ainda não liberado para a sua Empresa.')
                }} 
                exibirAcoesDeCrud={false}
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                filtros={{
                    aoBuscar: handleBuscar,
                    datas: {
                        dataInicio,
                        dataFim,
                        onChangeDataInicio: (d) => { setIdPagamentoFiltro(null); setDataInicio(d); },
                        onChangeDataFim: (d) => { setIdPagamentoFiltro(null); setDataFim(d); }
                    },
                    dropdowns: [
                        {
                            key: 'status',
                            label: 'Status',
                            opcoes: opcoesStatus,
                            value: statusFiltro,
                            onChange: (v) => { setIdPagamentoFiltro(null); setStatusFiltro(v); }
                        }
                    ]
                }}
                acoesExtras={[
                    {
                        Icone: FaEye,
                        Texto: "Visualizar Detalhes",
                        Cor: 'gray',
                        OnClick: handleVisualizar,
                        Tamanho: 'small'
                    },
                    {
                        Icone: FaSync,
                        Texto: "Atualizar Status",
                        OnClick: handleConsultar,
                        Tamanho: 'small',
                        Condicao: (row: any) => row.status == StatusNFSe.Processando,
                        TooltipDisabled: "Só é possível atualizar o Status de Notas Fiscais em Processamento."
                    },
                    {
                        Icone: FaEdit,
                        Texto: "Editar Agendamento",
                        OnClick: handleEditarClick,
                        Tamanho: 'small',
                        Condicao: (row: any) => row.status == StatusNFSe.Agendada,
                        TooltipDisabled(entidade) {
                            return "Apenas Notas Fiscais agendadas podem ser Editadas!";
                        },
                    },
                    {
                        Icone: FaFileInvoiceDollar,
                        Texto: "Re-Emitir (Erro)",
                        OnClick: (row) => {
                            if (row.status === StatusNFSe.Erro) {
                                handleAbrirEmitir(row);
                            } else {
                                toast.info("Apenas notas com erro podem ser re-emitidas por aqui.");
                            }
                        },
                        Tamanho: 'small',
                        Condicao: (row: any) => row.status == StatusNFSe.Erro,
                        TooltipDisabled: "Apenas Notas Fiscais com erro podem ser reemitidas."
                    },
                    {
                        Icone: FaFileCode,
                        Texto: "Baixar XML",
                        OnClick: handleBaixarXml,
                        Tamanho: 'small',
                        Condicao: (row: any) => row.status == StatusNFSe.Emitida || row.status == StatusNFSe.Cancelada,
                        TooltipDisabled: "Só é possível baixar o XML de uma Nota Fiscal Baixada ou Cancelada"                    
                    },
                    {
                        Icone: FaBan,
                        Texto: "Cancelar",
                        OnClick: handleCancelarClick,
                        Tamanho: 'small',
                        Condicao: (row: any) => row.status == StatusNFSe.Emitida || row.status == StatusNFSe.Agendada,
                        TooltipDisabled: "Só é possível cancelar uma Nota Fiscal que foi emitida ou agendada."
                    }
                ]}
                handleEditar={() => {}}
                handleExclusao={() => {}}
                handleVisualizacao={handleVisualizar}
                handleSelecionarEntidade={() => {}}
             />
             <ModalEmitirNota 
                show={showModalEmitir}
                onHide={() => setShowModalEmitir(false)}
                onEmitir={onEmitir}
                carregando={loadingEmission}
             />
             <ModalDetalhesNota 
                show={showModalDetalhes}
                onHide={() => setShowModalDetalhes(false)}
                nota={notaSelecionada}
             />
             <ModalNovaNota
                show={showModalNovaNota}
                onHide={() => setShowModalNovaNota(false)}
                onSuccess={carregarDados}
             />
             <ModalEditarAgendamento
                show={showModalEditarAgendamento}
                onHide={() => setShowModalEditarAgendamento(false)}
                onSuccess={carregarDados}
                nota={notaSelecionada}
             />
             <ModalCancelarNota
                show={showModalCancelar}
                onHide={() => setShowModalCancelar(false)}
                nota={notaParaCancelar}
                onConfirm={handleConfirmarCancelamento}
             />
        </LocaSmartLayout>
    );
};

export default NotasFiscaisListagem;